import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  CardContent,
  ToggleButton,
  ToggleButtonGroup
} from "@mui/material";
import { StyledCard, MobileCard } from "../styled";

const FIELD_ORDER = [
  "bar_name",
  "address",
  "bar_type",
  "description",
  "phone",
  "website"
];

const ConflictsTable = ({
  conflicts,
  fieldSelections,
  onFieldSelection,
  conflictResolutions,
  onConflictResolutionChange,
  barsToExclude,
  onToggleExclude,
  isMobile
}) => {
  const [pendingKeepBoth, setPendingKeepBoth] = useState(null);

  const handleResolutionChange = (conflict, value) => {
    if (!value) return;
    if (value === "insert") {
      setPendingKeepBoth(conflict);
    } else {
      onConflictResolutionChange(conflict.id, value);
    }
  };

  const handleConfirmKeepBoth = () => {
    if (pendingKeepBoth) {
      onConflictResolutionChange(pendingKeepBoth.id, "insert");
    }
    setPendingKeepBoth(null);
  };

  return (
    <>
      {isMobile ? (
        <MobileConflictsView
          conflicts={conflicts}
          fieldSelections={fieldSelections}
          onFieldSelection={onFieldSelection}
          conflictResolutions={conflictResolutions}
          onResolutionChange={handleResolutionChange}
          barsToExclude={barsToExclude}
          onToggleExclude={onToggleExclude}
        />
      ) : (
        <DesktopConflictsGrid
          conflicts={conflicts}
          fieldSelections={fieldSelections}
          onFieldSelection={onFieldSelection}
          conflictResolutions={conflictResolutions}
          onResolutionChange={handleResolutionChange}
          barsToExclude={barsToExclude}
          onToggleExclude={onToggleExclude}
        />
      )}

      <Dialog open={!!pendingKeepBoth} onClose={() => setPendingKeepBoth(null)}>
        <DialogTitle>Keep Both Bars?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will remove <strong>{pendingKeepBoth?.osmRecord?.bar_name || "this bar"}</strong> from
            the conflict list and keep both bars as-is. The OSM bar will be inserted as a new record
            alongside the existing one. Do you want to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingKeepBoth(null)}>Cancel</Button>
          <Button onClick={handleConfirmKeepBoth} variant="contained" color="primary">
            Proceed
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

/* ── Mobile card-based view ── */
const MobileConflictsView = ({
  conflicts,
  fieldSelections,
  onFieldSelection,
  conflictResolutions,
  onResolutionChange,
  barsToExclude,
  onToggleExclude
}) => (
  <Box display="flex" flexDirection="column" gap={2}>
    {conflicts.map((conflict) => {
      const resolution = conflictResolutions?.[conflict.id] || "merge";
      const isExcluded = barsToExclude?.has(conflict.osmRecordId);
      const conflictingFields = FIELD_ORDER.filter(
        (f) => conflict.fields[f]?.hasConflict
      );

      return (
        <MobileCard
          key={conflict.id}
          style={{
            borderColor: isExcluded ? "#1976d2" : undefined,
            opacity: isExcluded ? 0.7 : 1
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              mb: 1
            }}
          >
            <Checkbox
              size="small"
              checked={isExcluded}
              onChange={() => onToggleExclude(conflict.osmRecordId)}
              sx={{ mt: -0.5, ml: -0.5 }}
            />
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", wordBreak: "break-word", flex: 1, minWidth: 0 }}>
              {conflict.osmRecord.bar_name || "Unnamed"}
            </Typography>
          </Box>

          {/* Resolution toggle */}
          <ToggleButtonGroup
            exclusive
            size="small"
            color="primary"
            fullWidth
            value={resolution}
            onChange={(_, value) => onResolutionChange(conflict, value)}
            sx={{ mb: 1 }}
          >
            <ToggleButton value="merge" sx={{ fontSize: "0.75rem", textTransform: "none" }}>
              Update Existing
            </ToggleButton>
            <ToggleButton value="insert" sx={{ fontSize: "0.75rem", textTransform: "none" }}>
              Keep Both
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Conflicting fields — tap to pick */}
          {conflictingFields.length > 0 && resolution === "merge" && (
            <Box display="flex" flexDirection="column" gap={1} mt={0.5}>
              {conflictingFields.map((fieldName) => {
                const field = conflict.fields[fieldName];
                const selected = fieldSelections[conflict.id]?.[fieldName];
                return (
                  <MobileFieldPicker
                    key={fieldName}
                    label={formatHeader(fieldName)}
                    newValue={field.new}
                    existingValue={field.existing}
                    selected={selected}
                    onSelect={(src) => onFieldSelection(conflict.id, fieldName, src)}
                  />
                );
              })}
            </Box>
          )}

          {resolution === "insert" && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, fontStyle: "italic" }}>
              Keeping both — OSM bar will be added as a new record alongside the existing one.
            </Typography>
          )}
        </MobileCard>
      );
    })}
  </Box>
);

/* ── Single field picker (mobile) ── */
const MobileFieldPicker = ({ label, newValue, existingValue, selected, onSelect }) => (
  <Box>
    <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: "block" }}>
      {label}
    </Typography>
    <Box display="flex" gap={0.5}>
      <Box
        onClick={() => onSelect("new")}
        sx={{
          flex: 1,
          p: 1,
          borderRadius: 1,
          fontSize: "0.8rem",
          wordBreak: "break-word",
          cursor: "pointer",
          backgroundColor: selected === "new" ? "#c8e6c9" : "#e3f2fd",
          border: selected === "new" ? "2px solid #4caf50" : !selected ? "2px solid #f44336" : "2px solid transparent",
          transition: "all 0.15s"
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.25, color: "#1565c0" }}>
          OSM
        </Typography>
        <Typography variant="body2" sx={{ fontSize: "0.8rem", wordBreak: "break-word" }}>
          {newValue || "—"}
        </Typography>
      </Box>
      <Box
        onClick={() => onSelect("existing")}
        sx={{
          flex: 1,
          p: 1,
          borderRadius: 1,
          fontSize: "0.8rem",
          wordBreak: "break-word",
          cursor: "pointer",
          backgroundColor: selected === "existing" ? "#c8e6c9" : "#c8e6c9",
          border: selected === "existing" ? "2px solid #4caf50" : !selected ? "2px solid #f44336" : "2px solid transparent",
          opacity: selected === "existing" ? 1 : 0.7,
          transition: "all 0.15s"
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.25, color: "#2e7d32" }}>
          DB
        </Typography>
        <Typography variant="body2" sx={{ fontSize: "0.8rem", wordBreak: "break-word" }}>
          {existingValue || "—"}
        </Typography>
      </Box>
    </Box>
  </Box>
);

/* ── Desktop grid view (unchanged logic) ── */
const DesktopConflictsGrid = ({
  conflicts,
  fieldSelections,
  onFieldSelection,
  conflictResolutions,
  onResolutionChange,
  barsToExclude,
  onToggleExclude
}) => (
  <StyledCard>
    <CardContent>
      <Box
        sx={{
          overflowX: "auto",
          border: "1px solid #e0e0e0",
          borderRadius: "8px"
        }}
      >
        <Box
          sx={{
            maxHeight: "700px",
            overflowY: "auto"
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "40px 180px 140px 90px repeat(6, 1fr)",
              gap: "1px",
              backgroundColor: "#f5f5f5",
              minWidth: "1650px"
            }}
          >
            <HeaderCell />
            <HeaderCell>Name</HeaderCell>
            <HeaderCell>Resolution</HeaderCell>
            <HeaderCell>Source</HeaderCell>
            {FIELD_ORDER.map((header) => (
              <HeaderCell key={header}>{formatHeader(header)}</HeaderCell>
            ))}

            {conflicts.map((conflict, index) => {
              const isExcluded = barsToExclude?.has(conflict.osmRecordId);
              return (
              <React.Fragment key={conflict.id}>
                <StickyCell gridRow="span 2">
                  <Checkbox
                    size="small"
                    checked={isExcluded}
                    onChange={() => onToggleExclude(conflict.osmRecordId)}
                  />
                </StickyCell>

                <StickyInfoCell>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {conflict.osmRecord.bar_name || "Unnamed"}
                  </Typography>
                </StickyInfoCell>

                <ResolutionCell>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    color="primary"
                    orientation="vertical"
                    fullWidth
                    value={conflictResolutions?.[conflict.id] || "merge"}
                    onChange={(_, value) => onResolutionChange(conflict, value)}
                  >
                    <ToggleButton value="merge" sx={{ fontSize: "0.7rem", py: 0.5, px: 1, textTransform: "none", lineHeight: 1.2 }}>Update Existing</ToggleButton>
                    <ToggleButton value="insert" sx={{ fontSize: "0.7rem", py: 0.5, px: 1, textTransform: "none", lineHeight: 1.2 }}>Keep Both</ToggleButton>
                  </ToggleButtonGroup>
                </ResolutionCell>

                <SourceCell variant="new">New</SourceCell>
                {FIELD_ORDER.map((fieldName) =>
                  renderFieldCell({
                    conflict,
                    fieldName,
                    fieldSelections,
                    onFieldSelection,
                    source: "new"
                  })
                )}

                <SourceCell variant="existing" bottomBorder={index !== conflicts.length - 1}>
                  Existing
                </SourceCell>
                {FIELD_ORDER.map((fieldName) =>
                  renderFieldCell({
                    conflict,
                    fieldName,
                    fieldSelections,
                    onFieldSelection,
                    source: "existing",
                    isLastRow: index === conflicts.length - 1
                  })
                )}
              </React.Fragment>
              );
            })}
          </Box>
        </Box>
      </Box>
    </CardContent>
  </StyledCard>
);

const renderFieldCell = ({
  conflict,
  fieldName,
  fieldSelections,
  onFieldSelection,
  source,
  isLastRow = false
}) => {
  const field = conflict.fields[fieldName];
  if (!field) {
    return (
      <ContentCell
        key={`${conflict.id}-${source}-${fieldName}`}
        bottomBorder={!isLastRow}
        background={source === "new" ? "#e3f2fd" : "#c8e6c9"}
      >
        —
      </ContentCell>
    );
  }

  const selectedSource = fieldSelections[conflict.id]?.[fieldName];
  const hasConflict = field.hasConflict;
  const isSelected = selectedSource === source;
  const isUnresolved = hasConflict && !selectedSource;

  const background = source === "new" ? "#e3f2fd" : "#c8e6c9";
  const hoverBackground = source === "new" ? "#a5d6a7" : "#a5d6a7";
  const unresolvedHover = "#ffb3ba";

  const value = source === "new" ? field.new : field.existing;

  return (
    <ContentCell
      key={`${conflict.id}-${source}-${fieldName}`}
      onClick={() => hasConflict && onFieldSelection(conflict.id, fieldName, source)}
      background={
        isSelected && hasConflict ? "#c8e6c9" : isUnresolved ? "#ffcdd2" : background
      }
      hoverBackground={hasConflict ? (isSelected ? hoverBackground : unresolvedHover) : undefined}
      border={isSelected && hasConflict ? "2px solid #4caf50" : isUnresolved ? "2px solid #f44336" : "none"}
      bottomBorder={!isLastRow}
      cursor={hasConflict ? "pointer" : "default"}
      scrollable={fieldName === "description"}
    >
      <Typography variant="body2" sx={{ fontSize: "0.85rem", wordBreak: "break-word" }}>
        {value || "—"}
      </Typography>
    </ContentCell>
  );
};

const formatHeader = (header) => {
  if (header === "bar_name") return "Name";
  if (header === "bar_type") return "Type";
  return header
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const HeaderCell = ({ children }) => (
  <Box
    sx={{
      p: 2,
      backgroundColor: "white",
      fontWeight: "bold",
      borderBottom: "2px solid #e0e0e0",
      position: "sticky",
      top: 0,
      zIndex: 10
    }}
  >
    {children}
  </Box>
);

const StickyCell = ({ children, gridRow }) => (
  <Box
    sx={{
      p: 2,
      backgroundColor: "white",
      borderBottom: "1px solid #e0e0e0",
      gridRow,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: "bold"
    }}
  >
    {children}
  </Box>
);

const StickyInfoCell = ({ children }) => (
  <Box
    sx={{
      p: 2,
      backgroundColor: "white",
      borderBottom: "1px solid #e0e0e0",
      gridRow: "span 2",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center"
    }}
  >
    {children}
  </Box>
);

const ResolutionCell = ({ children }) => (
  <Box
    sx={{
      p: 1,
      backgroundColor: "white",
      borderBottom: "1px solid #e0e0e0",
      gridRow: "span 2",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      gap: 0.5
    }}
  >
    {children}
  </Box>
);

const SourceCell = ({ children, variant, bottomBorder = true }) => {
  const backgroundColor = variant === "new" ? "#e3f2fd" : "#c8e6c9";

  return (
    <Box
      sx={{
        p: 1,
        backgroundColor,
        borderBottom: bottomBorder ? "2px solid #333" : "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
        {children}
      </Typography>
    </Box>
  );
};

const ContentCell = ({
  children,
  background,
  hoverBackground,
  border,
  bottomBorder = true,
  cursor = "default",
  onClick,
  scrollable = false
}) => (
  <Box
    onClick={onClick}
    sx={{
      p: 2,
      backgroundColor: background,
      borderBottom: bottomBorder ? "1px solid #e0e0e0" : "none",
      cursor,
      transition: "background-color 0.2s",
      border,
      ...(scrollable ? {
        maxHeight: 80,
        overflowY: "auto",
        "&::-webkit-scrollbar": { width: 4 },
        "&::-webkit-scrollbar-thumb": { backgroundColor: "#bdbdbd", borderRadius: 2 },
      } : {}),
      ...(hoverBackground && cursor === "pointer"
        ? {
            "&:hover": {
              backgroundColor: hoverBackground
            }
          }
        : {})
    }}
  >
    {children}
  </Box>
);

export default ConflictsTable;
